import { Box, FormHelperText, InputLabel, Typography, LinearProgress } from '@mui/material';
import { useDropzone, DropzoneOptions } from 'react-dropzone';
import { UploadFileOutlined } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { getFileSizeInUnits } from '../utils/files';

type FileDragAndDropFieldProps = {
  onDrop(files: File[]): void;
  onDropRejected?: DropzoneOptions['onDropRejected'];
  accept?: DropzoneOptions['accept'];
  multiple?: DropzoneOptions['multiple'];
  maxFiles?: DropzoneOptions['maxFiles'];
  fileSizeLimit?: DropzoneOptions['maxSize'];
  error?: string;
  isUploading?: boolean;
  label?: string;
  height?: number;
  showIcon?: boolean;
};

const FileDragAndDropField = ({
  multiple,
  onDrop,
  onDropRejected,
  accept,
  maxFiles,
  fileSizeLimit,
  error,
  isUploading,
  label,
  height = 260,
  showIcon = true,
}: FileDragAndDropFieldProps) => {
  const theme = useTheme();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple,
    accept,
    maxFiles,
    maxSize: fileSizeLimit,
    onDrop: (acceptedFiles) => onDrop?.(Array.from(acceptedFiles as unknown as FileList)),
    onDropRejected,
    disabled: isUploading,
  });

  return (
    <Box>
      {label && <InputLabel sx={{ mb: 0.5 }}>{label}</InputLabel>}
      <Box
        {...getRootProps()}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderWidth: 1,
          borderStyle: 'dashed',
          borderColor: isDragActive ? theme.palette.success.main : '#ccc',
          flexWrap: 'wrap',
          height,
          mt: '1.25rem',
          cursor: 'pointer',
        }}
      >
        <input {...getInputProps()} />
        <Box>
          {isDragActive ? (
            showIcon && <UploadFileOutlined sx={{ color: theme.palette.success.main, fontSize: '3.75rem' }} />
          ) : (
            <Box>
              {showIcon && (
                <Box sx={{ width: '100%', textAlign: 'center' }}>
                  <CloudUploadOutlinedIcon sx={{ fontSize: '3.75rem', color: '#999' }} />
                </Box>
              )}
              <Typography variant="body2">
                Drop files here or&nbsp;
                <Typography sx={{ textDecoration: 'underline', color: '#000' }} component="span">
                  click
                </Typography>
                &nbsp;to upload
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      {isUploading && (
        <Box sx={{ width: '100%', mt: 1 }}>
          <LinearProgress />
        </Box>
      )}
      <Box sx={{ mt: '0.25rem', display: 'flex', justifyContent: 'space-between' }}>
        {fileSizeLimit && (
          <Typography variant="body2" component="span">
            Max. size: {getFileSizeInUnits(fileSizeLimit)}
          </Typography>
        )}
        {accept && (
          <Typography variant="body2" component="span">
            File types: {[...new Set(Object.values(accept).flat())].join(', ')}
          </Typography>
        )}
      </Box>
      {error && (
        <FormHelperText error sx={{ mt: '0.25rem' }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  );
};

export default FileDragAndDropField;
